/*!
 * OutIn — Cart restore re-sync (checkout Back-button fix).
 *
 * Root cause this addresses: when a page is restored from the browser's
 * back/forward cache (bfcache) — e.g. clicking the browser Back button after
 * visiting Shopify checkout — nothing in this theme re-fetches the real cart
 * state or re-dispatches a cart-update event:
 *
 *   - snippets/theme-drawer.liquid's inline restore script only re-opens a
 *     drawer's [open] attribute from a sessionStorage flag on a fresh page
 *     parse. It never touches cart data, and doesn't run at all on a true
 *     bfcache restore (no HTML re-parses on restore).
 *   - assets/cart-icon.js is the theme's only other bfcache-aware code
 *     (`window.addEventListener('pageshow', ...)`), and it only refreshes the
 *     header cart-count bubble from a cached sessionStorage snapshot — and
 *     only if that snapshot is less than 10 seconds old. It never re-renders
 *     the cart-drawer or full cart-page contents, and can't correct a stale
 *     count once the snapshot goes past 10 seconds.
 *
 * So a restored page can go on showing whatever cart-drawer/cart-page markup
 * was in memory when the user left — including an incorrect "Your cart is
 * empty" state — even though the real, server-side cart is fine. (A separate,
 * related bug in the cross-document View Transition handling — see the
 * try/catch added to assets/view-transitions.js and assets/utilities.js in
 * this same pass — could independently leave a stale/half-updated UI in
 * place after a Back navigation; this file's job is to guarantee a correct
 * re-sync regardless of whichever mechanism left the UI stale.)
 *
 * Fix: on `pageshow` with `event.persisted === true` (a genuine bfcache
 * restore, not a fresh navigation), fetch the real `/cart.js` state and
 * dispatch the theme's own, real `CartLinesUpdateEvent` (@shopify/events) —
 * the exact event class and payload shape already used by
 * assets/component-cart-items.js's own updateQuantity() and by
 * assets/fino-add-to-cart.js. This file introduces no new rendering logic:
 * every existing listener already knows what to do with this event once
 * dispatched on `document` —
 *
 *   - cart-items-component (assets/component-cart-items.js) — whether it's
 *     the drawer's instance or the full /cart page's instance — re-renders
 *     itself from the live server section HTML via the section-rendering
 *     fallback path (sectionRenderer.renderSection) that #handleCartUpdate()
 *     already takes whenever detail.sections is not supplied, as it
 *     intentionally isn't here.
 *   - cart-icon (assets/cart-icon.js) updates the header badge from the
 *     live `cart.totalQuantity`, replacing its own stale/short-lived
 *     sessionStorage fallback with an authoritative value.
 *   - cart-drawer (assets/cart-drawer.js) re-syncs its empty/non-empty
 *     visual state and sticky-summary layout from the refreshed content.
 *
 * Loaded globally from layout/theme.liquid (every page, like
 * outin-home-quick-add.js), since the bug reproduces on the full /cart page
 * — which never renders a <cart-drawer> at all — just as much as on any
 * other page.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  resyncCartFromServer();
});

async function resyncCartFromServer() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);

    const cartJson = await response.json();

    // Resolved immediately: this is a pure re-sync from the current server
    // state, not a pending mutation, so there's nothing to await further.
    const settled = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
      detail: {
        source: 'cart-restore-sync',
        itemCount: cartJson.item_count,
        items: cartJson.items,
        didError: false,
      },
    });

    // Dispatched on `document` (not on any specific component), matching the
    // same external-dispatch pattern already used by fino-add-to-cart.js and
    // outin-home-quick-add.js, so every listening component receives it.
    // `detail.sections` is intentionally omitted — see file header.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'restore',
        lines: [],
        promise: settled,
      })
    );
  } catch (error) {
    console.warn('[cart-restore-sync] Failed to re-sync cart after bfcache restore:', error);
  }
}
